import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRazorpay } from 'react-razorpay';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { CreditCard, CheckCircle, XCircle, Loader2, Shield, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PaymentButton Component
 * Handles Razorpay payment flow
 * 
 * @param {string} paymentType - Key from FEE_STRUCTURE (e.g., 'CBEAM', 'accreditation_discounted')
 * @param {number} amount - Optional override amount in INR
 * @param {string} referenceId - Optional reference ID (application ID, enrollment ID)
 * @param {string} description - Optional description
 * @param {string} buttonText - Custom button text
 * @param {string} buttonVariant - Button variant (default, outline, etc.)
 * @param {function} onSuccess - Callback on successful payment
 * @param {function} onError - Callback on payment error
 * @param {boolean} disabled - Disable the button
 */
export function PaymentButton({
  paymentType,
  amount,
  referenceId,
  description,
  buttonText,
  buttonVariant = 'default',
  onSuccess,
  onError,
  disabled = false,
  className = '',
  children
}) {
  const { token, user } = useAuth();
  const { Razorpay, isLoading: razorpayLoading } = useRazorpay();
  const [loading, setLoading] = useState(false);
  const razorpayInstanceRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const isLoadingRef = useRef(false); // Track loading state without causing re-renders

  // Use children or buttonText, with fallback
  const displayText = children || buttonText || 'Pay Now';

  // Function to reset loading state - safe to call multiple times
  const resetLoadingState = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    setLoading(false);
    isLoadingRef.current = false;
    razorpayInstanceRef.current = null;
  }, []);

  // Setup MutationObserver to watch for Razorpay modal removal
  const setupModalObserver = useCallback(() => {
    // Cleanup any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Use MutationObserver to detect when Razorpay modal is removed
    observerRef.current = new MutationObserver((mutations) => {
      // Check if modal is still present
      const razorpayFrame = document.querySelector('.razorpay-container, .razorpay-checkout-frame, iframe[src*="razorpay"]');
      const razorpayBackdrop = document.querySelector('.razorpay-backdrop');
      
      // If no Razorpay elements exist and we're in loading state, reset
      if (!razorpayFrame && !razorpayBackdrop && isLoadingRef.current) {
        resetLoadingState();
      }
    });

    // Observe the entire body for DOM changes
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true
    });
  }, [resetLoadingState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetLoadingState();
      if (razorpayInstanceRef.current) {
        try {
          razorpayInstanceRef.current.close();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [resetLoadingState]);

  const handlePayment = useCallback(async () => {
    if (!token) {
      toast.error('Please login to make a payment');
      return;
    }

    // Prevent multiple clicks while loading - use ref for immediate check
    if (isLoadingRef.current) {
      return;
    }

    // Set loading state
    setLoading(true);
    isLoadingRef.current = true;

    // Setup MutationObserver to watch for modal close
    setupModalObserver();

    // Safety timeout - reset loading after 2 minutes if something goes wrong
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    loadingTimeoutRef.current = setTimeout(() => {
      resetLoadingState();
    }, 120000);

    try {
      // Close any existing Razorpay instance
      if (razorpayInstanceRef.current) {
        try {
          razorpayInstanceRef.current.close();
        } catch (e) {
          // Ignore close errors
        }
        razorpayInstanceRef.current = null;
      }

      // Create order
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        payment_type: paymentType || 'custom',
        amount: amount,
        reference_id: referenceId,
        description: description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const orderData = orderRes.data;

      // Initialize Razorpay
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'IMCAC',
        description: orderData.payment_name || description,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Payment successful!');
            resetLoadingState();
            onSuccess?.(response.razorpay_payment_id);
          } catch (err) {
            resetLoadingState();
            toast.error('Payment verification failed');
            onError?.('Payment verification failed');
          }
        },
        prefill: orderData.prefill,
        theme: {
          color: '#1B2A4A'
        },
        modal: {
          ondismiss: () => {
            // This callback may not always fire reliably
            // The MutationObserver serves as a backup
            resetLoadingState();
          },
          escape: true,
          backdropclose: true,
          confirm_close: false
        }
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstanceRef.current = razorpayInstance;
      
      razorpayInstance.on('payment.failed', (response) => {
        toast.error(response.error.description || 'Payment failed');
        onError?.(response.error.description || 'Payment failed');
        resetLoadingState();
      });
      
      razorpayInstance.open();

    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to initiate payment';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to initiate payment');
      onError?.(typeof errorMsg === 'string' ? errorMsg : 'Failed to initiate payment');
      resetLoadingState();
    }
  }, [Razorpay, token, paymentType, amount, referenceId, description, onSuccess, onError, resetLoadingState, setupModalObserver]);

  return (
    <Button
      onClick={handlePayment}
      disabled={disabled || loading || razorpayLoading}
      variant={buttonVariant}
      className={`rounded-sm ${className}`}
      data-testid="payment-button"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <CreditCard className="h-4 w-4 mr-2" />
      )}
      {displayText}
    </Button>
  );
}


/**
 * PaymentCard Component
 * Displays a fee card with payment button
 */
export function PaymentCard({
  title,
  description,
  amount,
  originalAmount,
  paymentType,
  referenceId,
  features = [],
  onSuccess,
  disabled = false
}) {
  const hasDiscount = originalAmount && originalAmount > amount;
  const discountPercent = hasDiscount ? Math.round((1 - amount / originalAmount) * 100) : 0;

  return (
    <Card className="imcac-card hover:border-[#C5A572] transition-colors" data-testid="payment-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg text-[#1B2A4A]">{title}</CardTitle>
          {hasDiscount && (
            <Badge className="bg-green-500 text-white">{discountPercent}% OFF</Badge>
          )}
        </div>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          {hasDiscount && (
            <p className="text-sm text-slate-400 line-through">₹{originalAmount.toLocaleString()}</p>
          )}
          <p className="text-3xl font-bold text-[#1B2A4A]">
            ₹{amount.toLocaleString()}
          </p>
        </div>

        {features.length > 0 && (
          <>
            <Separator className="my-3" />
            <ul className="space-y-2 mb-4">
              {features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </>
        )}

        <PaymentButton
          paymentType={paymentType}
          amount={amount}
          referenceId={referenceId}
          buttonText={`Pay ₹${amount.toLocaleString()}`}
          onSuccess={onSuccess}
          disabled={disabled}
          className="w-full bg-[#1B2A4A] text-white"
        />

        <div className="flex items-center justify-center gap-2 mt-3 text-xs text-slate-400">
          <Shield className="h-3 w-3" />
          <span>Secured by Razorpay</span>
        </div>
      </CardContent>
    </Card>
  );
}


/**
 * PaymentHistoryDialog Component
 * Shows user's payment history
 */
export function PaymentHistoryDialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPayments = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/payments/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(res.data.payments || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch when dialog opens
  if (open && loading && token) {
    fetchPayments();
  }

  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: 'bg-green-100 text-green-700', icon: CheckCircle },
      created: { bg: 'bg-blue-100 text-blue-700', icon: CreditCard },
      failed: { bg: 'bg-red-100 text-red-700', icon: XCircle },
      refunded: { bg: 'bg-amber-100 text-amber-700', icon: IndianRupee }
    };
    const style = styles[status] || styles.created;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} rounded-sm capitalize`}>
        <Icon className="h-3 w-3 mr-1" /> {status}
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Payment History</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : payments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CreditCard className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No payments yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((payment) => (
              <Card key={payment.id} className="border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-[#1B2A4A]">{payment.payment_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{payment.receipt_id}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(payment.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1B2A4A]">₹{payment.amount?.toLocaleString()}</p>
                      {getStatusBadge(payment.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default PaymentButton;
